import React, { createContext, useRef, useCallback, useMemo, ReactNode } from 'react'

export interface ActionsContextValue {
  /** All DOM nodes that should short‐circuit the Card’s own handlers */
  actionRefs: Set<HTMLElement>
  /** Call this when a slot mounts */
  register: (el: HTMLElement) => void
  /** Call this when it unmounts */
  unregister: (el: HTMLElement) => void
}

export const ActionsRefContext = createContext<ActionsContextValue>({
  actionRefs: new Set(),
  register: () => {},
  unregister: () => {},
})

interface ProviderProps {
  children: ReactNode
}

export function ActionsRefProvider({ children }: ProviderProps) {
  const actionRefs = useRef(new Set<HTMLElement>())

  const register = useCallback((el: HTMLElement) => {
    actionRefs.current.add(el)
  }, [])

  const unregister = useCallback((el: HTMLElement) => {
    actionRefs.current.delete(el)
  }, [])

  const value = useMemo(
    () => ({
      actionRefs: actionRefs.current,
      register,
      unregister,
    }),
    [register, unregister],
  )

  return <ActionsRefContext.Provider value={value}>{children}</ActionsRefContext.Provider>
}
